import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Box, Tabs, Tab, Paper, Typography, Avatar, Stack, Button, Skeleton} from '@mui/material'
import {ChevronRightRounded} from '@mui/icons-material'
import {
    TabUnassignedCaregivers,
    TabCancelled,
    TabCompleted,
    TabConflictSchedules,
    TabExceedingUnits,
    TabInProgress,
    TabNoShow,
    TabPendingApproval,
    TabScheduled,
} from '../../components'
import {useMobile} from '../../../../hooks'
import {dashboardApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {format} from 'date-fns'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../../../utils/helpers'
import {SkeletonContainer} from '../../../../components'

const countSkeleton = (
    <SkeletonContainer component="span">
        <Skeleton variant="text" width={28} sx={{fontSize: 20}} />
    </SkeletonContainer>
)

export default () => {
    const [tab, setTab] = useState(0)
    const [counts, setCounts] = useState(0)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()

    // A new day, branch or Refresh replaces the counts with the skeleton.
    useEffect(() => {
        setSkeleton(true)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getTodaysScheduleOverview(branchId, {date: format(dashboardDate, 'yyyy-MM-dd'), page_size: 1})
                    .then(
                        ({
                            data: {
                                data: {counts},
                            },
                        }) => {
                            if (id !== requestId.current) return
                            setCounts(counts)
                            setSkeleton(false)
                        },
                    )
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    return (
        <div className="container">
            <div className="title">
                <Typography variant="h6" color="text.primary">
                    Today's Schedule Overview
                </Typography>
                {!mobile && (
                    <div className="view-more">
                        <Typography variant="h8" color="primary">
                            View More
                        </Typography>
                        <ChevronRightRounded fontSize="medium" color="primary" />
                    </div>
                )}
            </div>
            <br />
            <Typography variant="body2" color="text.secondary">
                63 visits - Thu, 12 Jun 2026
            </Typography>
            <br />
            <Tabs
                value={tab}
                onChange={(e, newValue) => setTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{style: {display: 'none'}}}>
                <Tab
                    label={
                        <div className={tab === 0 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.unassigned || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Unassigned Caregivers
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 1 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.cancelled || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Cancelled
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 2 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.missed || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #No-show
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 3 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.conflict || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Conflict Schedules
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 4 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.aaa || '-'}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Exceeding Units
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 5 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.pending_approval || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Pending Approval
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 6 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.scheduled || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Scheduled
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 7 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.aaa || '-'}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #In Progress
                            </Typography>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={tab === 8 ? 'tab tab-active' : 'tab'}>
                            {skeleton ? (
                                countSkeleton
                            ) : (
                                <Typography variant="h6" color="#D32F2F">
                                    {counts?.completed || 0}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.primary">
                                #Completed
                            </Typography>
                        </div>
                    }
                />
            </Tabs>
            <div style={{display: tab != 0 ? 'none' : 'block'}}>
                <TabUnassignedCaregivers />
            </div>
            <div style={{display: tab != 1 ? 'none' : 'block'}}>
                <TabCancelled />
            </div>
            <div style={{display: tab != 2 ? 'none' : 'block'}}>
                <TabNoShow />
            </div>
            <div style={{display: tab != 3 ? 'none' : 'block'}}>
                <TabConflictSchedules />
            </div>
            <div style={{display: tab != 4 ? 'none' : 'block'}}>
                <TabExceedingUnits />
            </div>
            <div style={{display: tab != 5 ? 'none' : 'block'}}>
                <TabPendingApproval />
            </div>
            <div style={{display: tab != 6 ? 'none' : 'block'}}>
                <TabScheduled />
            </div>
            <div style={{display: tab != 7 ? 'none' : 'block'}}>
                <TabInProgress />
            </div>
            <div style={{display: tab != 8 ? 'none' : 'block'}}>
                <TabCompleted />
            </div>
        </div>
    )
}
